(function () {
  var SearchFormBase = typeof SearchForm !== 'undefined'
    ? SearchForm
    : class extends HTMLElement {
      constructor() {
        super();
        this.input = this.querySelector('input[type=search]');
        this.resetButton = this.querySelector('button[type=reset]');

        if (!this.input || !this.input.form) return;

        this.input.form.addEventListener('reset', this.onFormReset.bind(this));
        this.input.addEventListener('input', this.debounce(this.onChange.bind(this), 300));
      }

      debounce(fn, wait) {
        var timeout;

        return function () {
          var args = arguments;
          window.clearTimeout(timeout);
          timeout = window.setTimeout(function () {
            fn.apply(this, args);
          }.bind(this), wait);
        }.bind(this);
      }

      toggleResetButton() {
        if (!this.resetButton || !this.input) return;

        var resetIsHidden = this.resetButton.classList.contains('hidden');
        if (this.input.value.length > 0 && resetIsHidden) {
          this.resetButton.classList.remove('hidden');
        } else if (this.input.value.length === 0 && !resetIsHidden) {
          this.resetButton.classList.add('hidden');
        }
      }

      onChange() {
        this.toggleResetButton();
      }

      shouldResetForm() {
        return !document.querySelector('[aria-selected=true] a');
      }

      onFormReset(event) {
        event.preventDefault();
        if (!this.input || !this.shouldResetForm()) return;

        this.input.value = '';
        this.input.focus();
        this.toggleResetButton();
      }
    };

  if (typeof SearchForm === 'undefined' && !customElements.get('search-form')) {
    customElements.define('search-form', SearchFormBase);
  }

  class PredictiveSearch extends SearchFormBase {
    constructor() {
      super();
      if (!this.input || !this.input.form) return;

      this.cachedResults = {};
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
      this.isOpen = false;
      this.abortController = new AbortController();
      this.searchTerm = '';

      this.setupEventListeners();
    }

    setupEventListeners() {
      this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
      this.input.addEventListener('focus', this.onFocus.bind(this));
      this.addEventListener('focusout', this.onFocusOut.bind(this));
      this.addEventListener('keyup', this.onKeyup.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
    }

    getQuery() {
      return this.input.value.trim();
    }

    onChange() {
      super.onChange();

      var newSearchTerm = this.getQuery();
      var resultsWrapper = this.querySelector('#predictive-search-results-groups-wrapper');
      if ((!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) && resultsWrapper) {
        resultsWrapper.remove();
      }

      this.updateSearchForTerm(this.searchTerm, newSearchTerm);
      this.searchTerm = newSearchTerm;

      if (!this.searchTerm.length) {
        this.close(true);
        return;
      }

      this.getSearchResults(this.searchTerm);
    }

    onFormSubmit(event) {
      if (!this.getQuery().length || this.querySelector('[aria-selected=true] a')) {
        event.preventDefault();
      }
    }

    onFormReset(event) {
      super.onFormReset(event);
      if (!super.shouldResetForm()) return;

      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }

    onFocus() {
      var currentSearchTerm = this.getQuery();
      if (!currentSearchTerm.length) return;

      if (this.searchTerm !== currentSearchTerm) {
        this.onChange();
      } else if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getSearchResults(this.searchTerm);
      }
    }

    onFocusOut() {
      window.setTimeout(function () {
        if (!this.contains(document.activeElement)) this.close();
      }.bind(this));
    }

    onKeyup(event) {
      if (!this.getQuery().length) this.close(true);
      event.preventDefault();

      if (event.code === 'ArrowUp') {
        this.switchOption('up');
      } else if (event.code === 'ArrowDown') {
        this.switchOption('down');
      } else if (event.code === 'Enter') {
        this.selectOption();
      }
    }

    onKeydown(event) {
      if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
        event.preventDefault();
      }
    }

    updateSearchForTerm(previousTerm, newTerm) {
      var searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
      var currentButtonText = searchForTextElement && searchForTextElement.innerText;
      if (!currentButtonText || !previousTerm) return;

      if ((currentButtonText.match(new RegExp(previousTerm, 'g')) || []).length > 1) return;
      searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
    }

    switchOption(direction) {
      if (!this.getAttribute('open')) return;

      var moveUp = direction === 'up';
      var selectedElement = this.querySelector('[aria-selected=true]');
      var allVisibleElements = Array.prototype.slice.call(
        this.querySelectorAll('li, button.predictive-search__item')
      ).filter(function (element) {
        return element.offsetParent !== null;
      });
      var activeElementIndex = 0;

      if (moveUp && !selectedElement) return;

      var selectedElementIndex = allVisibleElements.indexOf(selectedElement);
      if (!moveUp && selectedElement) {
        activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
      } else if (moveUp) {
        activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
      }
      if (activeElementIndex === selectedElementIndex) return;

      var activeElement = allVisibleElements[activeElementIndex];
      if (!activeElement) return;

      activeElement.setAttribute('aria-selected', 'true');
      if (selectedElement) selectedElement.setAttribute('aria-selected', 'false');
      this.input.setAttribute('aria-activedescendant', activeElement.id);
    }

    selectOption() {
      var selectedOption = this.querySelector('[aria-selected=true] a, button[aria-selected=true]');
      if (selectedOption) selectedOption.click();
    }

    getSearchResults(searchTerm) {
      var queryKey = searchTerm.replace(' ', '-').toLowerCase();
      this.setLiveRegionLoadingState();

      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }

      fetch(window.routes.predictive_search_url + '?q=' + encodeURIComponent(searchTerm) + '&section_id=predictive-search', {
        signal: this.abortController.signal
      })
        .then(function (response) {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then(function (text) {
          var container = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
          var resultsMarkup = container ? container.innerHTML : '';

          this.allPredictiveSearchInstances.forEach(function (predictiveSearchInstance) {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          });
          this.renderSearchResults(resultsMarkup);
        }.bind(this))
        .catch(function (error) {
          if (error && error.code === 20) return;
          this.close();
          throw error;
        }.bind(this));
    }

    setLiveRegionLoadingState() {
      this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
      this.loadingText = this.loadingText || this.getAttribute('data-loading-text') || '';
      this.setLiveRegionText(this.loadingText);
      this.setAttribute('loading', 'true');
    }

    setLiveRegionText(statusText) {
      if (!this.statusElement) return;

      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;

      window.setTimeout(function () {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }.bind(this), 1000);
    }

    renderSearchResults(resultsMarkup) {
      if (!this.predictiveSearchResults) return;

      this.predictiveSearchResults.innerHTML = resultsMarkup;
      this.setAttribute('results', 'true');
      this.setLiveRegionResults();
      this.open();
    }

    setLiveRegionResults() {
      var countValue = this.querySelector('[data-predictive-search-live-region-count-value]');
      this.removeAttribute('loading');
      this.setLiveRegionText(countValue ? countValue.textContent : '');
    }

    getResultsMaxHeight() {
      var header = document.querySelector('.section-header') || document.querySelector('.ddp-header');
      var headerBottom = header ? header.getBoundingClientRect().bottom : 0;
      this.resultsMaxHeight = window.innerHeight - headerBottom;
      return this.resultsMaxHeight;
    }

    open() {
      if (!this.predictiveSearchResults) return;

      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || this.getResultsMaxHeight() + 'px';
      this.setAttribute('open', 'true');
      this.input.setAttribute('aria-expanded', 'true');
      this.isOpen = true;
    }

    close(clearSearchTerm) {
      this.closeResults(clearSearchTerm);
      this.isOpen = false;
    }

    closeResults(clearSearchTerm) {
      if (clearSearchTerm) {
        this.input.value = '';
        this.removeAttribute('results');
      }

      var selected = this.querySelector('[aria-selected=true]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      this.removeAttribute('loading');
      this.removeAttribute('open');
      this.resultsMaxHeight = false;
      if (this.predictiveSearchResults) this.predictiveSearchResults.removeAttribute('style');
    }
  }

  if (!customElements.get('predictive-search')) {
    customElements.define('predictive-search', PredictiveSearch);
  }
})();